/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

import React, { useMemo, useState } from 'react';
import { FaFilePdf } from 'react-icons/fa';
import { TbJson } from 'react-icons/tb';
import { PiFileCsv } from 'react-icons/pi';
import {
  LuDownload,
  LuFileText,
  LuAlertCircle,
  LuList,
  LuFileSpreadsheet,
  LuFileJson,
  LuFileCheck2,
  LuRefreshCw,
} from 'react-icons/lu';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { Modal } from '@/components/elements/Modal';
import { AppButton } from '@/components/atoms/AppButton';
import AppRadio from '@/components/atoms/AppRadio';
import {
  APP_BG,
  APP_BORDER,
  APP_SEMANTIC,
} from '@/constant/theme/colors.constant';
import Spinner from '@/components/atoms/Spinner';
import SelectableChip from '@/components/elements/SelectableChip';
import { Text } from '@/components/atoms/AppText';
import { useTheme } from '@/context/theme.context';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import {
  generateItemsRangeByFormat,
  getMaxLimitByFormat,
} from './export.utils';
import { EAllowedFieldsForExport, EExportPage } from './export.types';
import { tid } from '@/utils/testId';
import { ORGANISM_TEST_IDS } from '@/constant/testIds/oganism.testIds';

/* ── Format meta ──────────────────────────────────────────────────── */
const FMETA: Record<
  string,
  {
    icon: React.ElementType;
    desc: string;
    color: string;
    bg: string;
  }
> = {
  CSV: {
    icon: LuFileSpreadsheet,
    desc: ORGANISM_PROPS.EXPORT_MODAL.CSV_DESC,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50 dark:bg-emerald-500/10',
  },
  PDF: {
    icon: FaFilePdf,
    desc: ORGANISM_PROPS.EXPORT_MODAL.PDF_DESC,
    color: 'text-rose-500',
    bg: 'bg-rose-50 dark:bg-rose-500/10',
  },
  JSON: {
    icon: LuFileJson,
    desc: ORGANISM_PROPS.EXPORT_MODAL.JSON_DESC,
    color: 'text-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-500/10',
  },
};

const { EXPORT_MODAL_TID } = ORGANISM_TEST_IDS;

/* ── Section wrapper ──────────────────────────────────────────────── */
const Section = ({
  step,
  done,
  label,
  sublabel,
  headerRight,
  children,
}: {
  step: number;
  done: boolean;
  label: string;
  sublabel?: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-2">
    {/* Header row */}
    <div className="flex items-center gap-2.5">
      <div
        className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black flex-shrink-0 transition-all duration-200 ${
          done
            ? 'bg-primary text-white shadow shadow-primary/30'
            : `${APP_SEMANTIC.neutral.bg} text-gray-400 dark:text-gray-500`
        }`}
      >
        {done ? <LuFileCheck2 className="w-3 h-3" /> : step}
      </div>
      <div className="flex-1 min-w-0">
        <Text
          as="span"
          size="xs"
          weight="black"
          color={done ? 'primary' : 'subtle'}
          tracking="widest"
          className="uppercase"
        >
          {label}
        </Text>
        {sublabel && (
          <Text as="p" size="xs" color="faint" className="mt-0">
            {sublabel}
          </Text>
        )}
      </div>
      {headerRight && <div className="flex-shrink-0">{headerRight}</div>}
    </div>
    {/* Content indented under step */}
    <div className="ml-[30px]">{children}</div>
  </div>
);

/* ── Thin separator ───────────────────────────────────────────────── */
const Sep = () => (
  <div className="h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-white/[0.06] to-transparent" />
);

type TRange = { id: string; name: string; maxLimit: number; page: number };

type TExportModalProps = {
  open: boolean;
  onClose: () => void;
  totalRecords: number;
  allowedFormats: { id: string; name: string }[];
  onGenerate: (payload: any) => Promise<void>;
  modalTitle?: string;
  selectiveFieldExport: boolean;
  allowedFieldsForExport: string[];
};

export const ExportDataModal = ({
  open,
  onClose,
  totalRecords,
  allowedFormats,
  onGenerate,
  modalTitle = 'Data',
  selectiveFieldExport = false,
  allowedFieldsForExport = [],
}: TExportModalProps) => {
  useTheme();

  const [documentFormat, setDocumentFormat] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [range, setRange] = useState<TRange | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeFields, setActiveFields] = useState<string[]>(
    allowedFieldsForExport,
  );

  const maxLimit = getMaxLimitByFormat(documentFormat);

  const rangeOptions: TRange[] = useMemo(() => {
    if (!documentFormat) return [];
    return (generateItemsRangeByFormat(documentFormat, totalRecords) ?? []).map(
      (r: any, idx: number) => ({
        id: r?.label,
        name: r?.label,
        page: idx + 1,
        maxLimit,
      }),
    );
  }, [documentFormat, totalRecords, maxLimit]);

  const toggleField = (f: string) =>
    setActiveFields((prev) =>
      prev.includes(f)
        ? prev.length > 1
          ? prev.filter((x) => x !== f)
          : prev
        : [...prev, f],
    );

  const handleReset = () => {
    setDocumentFormat(null);
    setRange(null);
    setActiveFields(allowedFieldsForExport);
  };

  const handleGenerate = async () => {
    if (!documentFormat || !range) return;
    setLoading(true);
    try {
      await onGenerate({
        documentFormat: documentFormat?.name,
        page: range?.page,
        limit: range?.maxLimit,
        fields: activeFields,
      });
      handleReset();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const hasRecords = totalRecords > 0;
  const canGenerate =
    !!documentFormat &&
    !!range &&
    hasRecords &&
    (!selectiveFieldExport || activeFields.length > 0);
  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      variant="default"
      size={selectiveFieldExport ? '2xl' : 'sm'}
    >
      <Modal.Header
        title={
          hasRecords
            ? `Export ${modalTitle}`
            : ORGANISM_PROPS.EXPORT_MODAL.NO_RECORDS_TITLE
        }
        subtitle={
          hasRecords
            ? `${totalRecords.toLocaleString()} records available`
            : ORGANISM_PROPS.EXPORT_MODAL.NO_RECORDS_SUBTITLE
        }
        icon={<LuDownload className="w-4 h-4" />}
        onClose={handleClose}
      />

      <Modal.Body>
        {!hasRecords ? (
          /* ── Empty state ── */
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <div
              className={`w-14 h-14 rounded-2xl ${APP_BG.muted} flex items-center justify-center`}
            >
              <LuAlertCircle className="w-7 h-7 text-gray-300 dark:text-gray-600" />
            </div>
            <div>
              <Text as="p" size="base" weight="semibold" color="soft">
                {ORGANISM_PROPS.EXPORT_MODAL.NO_RECORDS_EMPTY_STATE}
              </Text>
              <Text as="p" size="md" color="faint" className="mt-0.5">
                {ORGANISM_PROPS.EXPORT_MODAL.NO_RECORDS_HINT}
              </Text>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* ══ STEP 1 — FORMAT ═════════════════════════════════════ */}
            <Section
              step={1}
              done={!!documentFormat}
              label={ORGANISM_PROPS.EXPORT_MODAL.FORMAT_LABEL}
            >
              <div className="flex flex-col gap-1.5">
                {(allowedFormats ?? []).map((fmt: any) => {
                  const obj =
                    fmt?.id !== undefined ? fmt : { id: fmt, name: fmt };
                  const key = (obj.name ?? obj.id ?? '').toUpperCase();
                  const m = FMETA[key] ?? {
                    icon: LuFileText,
                    desc: '',
                    color: 'text-gray-400',
                    bg: 'bg-gray-100 dark:bg-white/[0.06]',
                  };
                  const sel = documentFormat?.id === obj.id;
                  const Icon = m.icon;

                  return (
                    <button
                      key={obj.id}
                      data-testid={tid(`${EXPORT_MODAL_TID.FORMAT}-${obj.id}`)}
                      type="button"
                      onClick={() => {
                        setDocumentFormat(obj);
                        setRange(null);
                      }}
                      className={`group flex items-center gap-3 w-full px-3.5 py-2.5 rounded-xl border transition-all duration-200 focus:outline-none select-none text-left ${
                        sel
                          ? 'border-primary/40 bg-primary/5 dark:bg-primary/10 shadow-sm'
                          : `${APP_BORDER.card} ${APP_BG.base} hover:border-gray-300 dark:hover:border-white/[0.14] hover:bg-gray-50/60 dark:hover:bg-white/[0.04]`
                      }`}
                    >
                      {/* File icon badge */}
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${sel ? 'bg-primary/15 dark:bg-primary/20' : m.bg}`}
                      >
                        <Icon
                          className={`w-5 h-5 ${sel ? 'text-primary' : m.color}`}
                        />
                      </div>

                      {/* Label + desc */}
                      <div className="flex-1 min-w-0">
                        <Text
                          as="p"
                          size="base"
                          weight="bold"
                          color={sel ? 'primary' : 'default'}
                          className="leading-none"
                        >
                          {key}
                        </Text>
                        <Text
                          as="p"
                          size="sm"
                          color={sel ? 'primary' : 'faint'}
                          className="mt-0.5 leading-none"
                        >
                          {m.desc}
                        </Text>
                      </div>

                      {/* AppRadio indicator */}
                      <AppRadio
                        checked={sel}
                        onChange={() => {
                          setDocumentFormat(obj);
                          setRange(null);
                        }}
                        size="sm"
                        color="primary"
                        variant={sel ? 'filled' : 'outlined'}
                        className="pointer-events-none flex-shrink-0"
                        testId={tid(`${EXPORT_MODAL_TID.FORMAT}-${obj.id}`)}
                      />
                    </button>
                  );
                })}
              </div>
            </Section>

            {/* ══ STEP 2 — RANGE ══════════════════════════════════════ */}
            {documentFormat && (
              <>
                <Sep />
                <Section
                  step={2}
                  done={!!range}
                  label={ORGANISM_PROPS.EXPORT_MODAL.RANGE_LABEL}
                  sublabel={`Max ${maxLimit?.toLocaleString()} records per file`}
                >
                  <div className="flex flex-wrap gap-1.5">
                    {rangeOptions.map((r) => (
                      <SelectableChip
                        key={r.id}
                        label={r.name}
                        selected={range?.id === r.id}
                        onClick={() => setRange(r)}
                        selectionType="radio"
                        theme="primary"
                        testId={tid(`${EXPORT_MODAL_TID.RANGE}-${documentFormat?.id}`)}
                      />
                    ))}
                  </div>
                </Section>
              </>
            )}

            {/* ══ STEP 3 — COLUMNS ════════════════════════════════════ */}
            {selectiveFieldExport && documentFormat && (
              <>
                <Sep />
                <Section
                  step={3}
                  done={activeFields.length > 0}
                  label={ORGANISM_PROPS.EXPORT_MODAL.COLUMNS_LABEL}
                  sublabel={`${activeFields.length} of ${allowedFieldsForExport.length} selected`}
                  headerRight={
                    <div className="flex items-center gap-1">
                      <AppButton
                        variant="ghost"
                        size="xs"
                        shape="pill"
                        ripple={false}
                        onClick={() => setActiveFields(allowedFieldsForExport)}
                        disabled={
                          activeFields.length === allowedFieldsForExport.length
                        }
                        dataTestId={tid(EXPORT_MODAL_TID.ALL_BTN)}
                      >
                        {ORGANISM_PROPS.EXPORT_MODAL.ALL}
                      </AppButton>
                      <AppButton
                        variant="ghost"
                        size="xs"
                        shape="pill"
                        ripple={false}
                        onClick={() =>
                          setActiveFields(allowedFieldsForExport.slice(0, 1))
                        }
                        disabled={activeFields.length <= 1}
                        dataTestId={tid(EXPORT_MODAL_TID.CLEAR_BTN)}
                      >
                        {ORGANISM_PROPS.EXPORT_MODAL.CLEAR}
                      </AppButton>
                    </div>
                  }
                >
                  {/* Chip grid */}
                  <div className="flex flex-wrap gap-1.5">
                    {allowedFieldsForExport.map((f) => (
                      <SelectableChip
                        key={f}
                        label={f}
                        selected={activeFields.includes(f)}
                        onClick={() => toggleField(f)}
                        selectionType="checkbox"
                        theme="primary"
                        testId={tid(`${EXPORT_MODAL_TID.SELECTABLE_COLUMNS}-${f}`)}
                      />
                    ))}
                  </div>

                  {activeFields.length === 0 && (
                    <div className="flex items-center gap-1.5 mt-2">
                      <LuAlertCircle className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" />
                      <Text as="span" size="sm" color="danger">
                        {ORGANISM_PROPS.EXPORT_MODAL.AT_LEAST_ONE_COLUMN}
                      </Text>
                    </div>
                  )}
                </Section>
              </>
            )}
          </div>
        )}

        {/* Loading overlay */}
        {loading && (
          <div
            className={`absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 rounded-2xl ${APP_BG.overlay} backdrop-blur-sm`}
          >
            <Spinner size="lg" color="primary" />
            <Text as="p" size="md" weight="semibold" color="muted">
              {ORGANISM_PROPS.EXPORT_MODAL.GENERATING}
            </Text>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <AppButton
          variant="primary"
          tint
          size="sm"
          icon={<LuRefreshCw />}
          disabled={loading || !hasRecords}
          ripple={false}
          onClick={handleReset}
          dataTestId={tid(EXPORT_MODAL_TID.RESET_BTN)}

        >
          {ORGANISM_PROPS.EXPORT_MODAL.RESET}
        </AppButton>
        <AppButton
          variant="primary"
          size="sm"
          disabled={!canGenerate || loading}
          loading={loading}
          icon={<LuDownload />}
          dataTestId={tid(EXPORT_MODAL_TID.GENERATE_BTN)}
          onClick={handleGenerate}
        >
          {ORGANISM_PROPS.EXPORT_MODAL.EXPORT}
        </AppButton>
      </Modal.Footer>
    </Modal>
  );
};
